import React, { useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { getTrialStatusDisplay } from '../../utils/trialAccess';
import TourModeActionButton from './TourModeActionButton';

const OrganizationTrialSection = ({
  organization,
  canManageTrial = false,
  isTour = false,
  isMutatingTrial = false,
  onMarkTrial,
  onExtendTrial,
  onForceExpire,
  onActivatePermanently,
}) => {
  const { t } = useTranslation();
  const trialAccess = getTrialStatusDisplay(organization);
  const trialModal = useDisclosure();
  const [modalAction, setModalAction] = useState(null);
  const [days, setDays] = useState('14');

  const canMarkAsTrial = !trialAccess.isPermanent && !trialAccess.isTrial;
  const canExtendTrial = trialAccess.isTrial && !trialAccess.accessBlocked;
  const canForceExpire = trialAccess.isTrial && !trialAccess.accessBlocked;
  const canActivatePermanently = !trialAccess.isPermanent && trialAccess.hasKnownState;

  const openDaysModal = (action) => {
    setModalAction(action);
    setDays(String(trialAccess.daysRemaining ?? 14));
    trialModal.onOpen();
  };

  const closeDaysModal = () => {
    if (isMutatingTrial) return;
    setModalAction(null);
    trialModal.onClose();
  };

  const handleDaysSubmit = async (event) => {
    event.preventDefault();

    const parsedDays = Number(days);
    if (!Number.isFinite(parsedDays) || parsedDays <= 0) {
      return;
    }

    if (modalAction === 'mark_trial' && onMarkTrial) {
      await onMarkTrial(parsedDays);
    }

    if (modalAction === 'extend_trial' && onExtendTrial) {
      await onExtendTrial(parsedDays);
    }

    closeDaysModal();
  };

  const getStatusLabel = () => {
    if (trialAccess.trialStatus === 'pending') return t('trial_access.status.pending');
    if (trialAccess.trialStatus === 'active') return t('trial_access.status.active');
    if (trialAccess.trialStatus === 'expired') return t('trial_access.status.expired');
    return t('trial_access.status.none');
  };

  const daysRemainingText = (() => {
    if (trialAccess.trialStatus !== 'active' || trialAccess.daysRemaining == null) return null;
    if (trialAccess.daysRemaining === 1) return t('trial_access.organization.one_day_remaining');
    return t('trial_access.organization.multiple_days_remaining', { count: trialAccess.daysRemaining });
  })();

  return (
    <Card>
      <CardBody p={0}>
        <Accordion allowToggle defaultIndex={0} reduceMotion>
          <AccordionItem border="none">
            <AccordionButton py={4} px={6} _expanded={{ bg: 'gray.50' }}>
              <Box flex="1" textAlign="left">
                <HStack justify="space-between" align="center" pr={2} flexWrap="wrap" spacing={3}>
                  <Heading size="md">{t('trial_access.organization.title')}</Heading>
                  <Badge colorScheme={trialAccess.colorScheme} variant="subtle" px={2.5} py={1} borderRadius="full">
                    {getStatusLabel()}
                  </Badge>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={5} px={6}>
              <VStack align="start" spacing={3}>
                <Text>
                  <strong>{t('trial_access.organization.state_label')}:</strong>{' '}
                  {getStatusLabel()}
                </Text>
                {daysRemainingText ? (
                  <Text>
                    <strong>{t('trial_access.organization.days_remaining_label')}:</strong>{' '}
                    {daysRemainingText}
                  </Text>
                ) : null}
                <Text>
                  <strong>{t('trial_access.organization.started_label')}:</strong>{' '}
                  {trialAccess.startedAt || t('trial_access.organization.not_provided')}
                </Text>
                <Text>
                  <strong>{t('trial_access.organization.expires_label')}:</strong>{' '}
                  {trialAccess.expiresAt || t('trial_access.organization.not_provided')}
                </Text>
                <Text>
                  <strong>{t('trial_access.organization.access_label')}:</strong>{' '}
                  {trialAccess.accessBlocked
                    ? t('trial_access.organization.locked')
                    : t('trial_access.organization.full_access')}
                </Text>
                {!trialAccess.hasKnownState ? (
                  <Text color="gray.500">{t('trial_access.organization.pending_backend')}</Text>
                ) : null}

                {canManageTrial ? (
                  <HStack spacing={2} flexWrap="wrap" pt={2}>
                    <TourModeActionButton
                      size="sm"
                      variant="outline"
                      onClick={() => openDaysModal('mark_trial')}
                      isTour={isTour}
                      isDisabled={!canMarkAsTrial}
                      isLoading={isMutatingTrial && modalAction === 'mark_trial'}
                    >
                      {t('trial_access.organization.mark_trial')}
                    </TourModeActionButton>
                    <TourModeActionButton
                      size="sm"
                      variant="outline"
                      onClick={() => openDaysModal('extend_trial')}
                      isTour={isTour}
                      isDisabled={!canExtendTrial}
                      isLoading={isMutatingTrial && modalAction === 'extend_trial'}
                    >
                      {t('trial_access.organization.adjust_trial')}
                    </TourModeActionButton>
                    <TourModeActionButton
                      size="sm"
                      variant="outline"
                      colorScheme="green"
                      onClick={onActivatePermanently}
                      isTour={isTour}
                      isDisabled={!canActivatePermanently || isMutatingTrial}
                    >
                      {t('trial_access.organization.activate_permanently')}
                    </TourModeActionButton>
                    <TourModeActionButton
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      onClick={onForceExpire}
                      isTour={isTour}
                      isDisabled={!canForceExpire || isMutatingTrial}
                    >
                      {t('trial_access.organization.force_expire')}
                    </TourModeActionButton>
                  </HStack>
                ) : null}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </CardBody>

      <Modal isOpen={trialModal.isOpen} onClose={closeDaysModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {modalAction === 'extend_trial'
              ? t('trial_access.organization.adjust_modal_title')
              : t('trial_access.organization.mark_modal_title')}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleDaysSubmit}>
            <ModalBody>
              <FormControl isRequired>
                <FormLabel htmlFor="trial-days-input">{t('trial_access.organization.duration_label')}</FormLabel>
                <Input
                  id="trial-days-input"
                  type="number"
                  min="1"
                  step="1"
                  value={days}
                  onChange={(event) => setDays(event.target.value)}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={closeDaysModal} isDisabled={isMutatingTrial}>
                {t('trial_access.organization.cancel')}
              </Button>
              <TourModeActionButton
                type="submit"
                colorScheme="blue"
                isTour={isTour}
                isLoading={isMutatingTrial}
                isDisabled={!days || Number(days) <= 0}
              >
                {modalAction === 'extend_trial'
                  ? t('trial_access.organization.confirm_adjust')
                  : t('trial_access.organization.confirm_mark')}
              </TourModeActionButton>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default OrganizationTrialSection;
